import { Inject, Injectable } from '@angular/core';
import { HoldingTableDetails } from '@ddt-web/blending';
import { APP_STORE_CONFIG_TOKEN } from '@ddt-web/config';
import { AnalysisParams, AppStoreConfig, DisplayDataFactoryService, PasFeature, PortfolioAnalysisParams } from '@ddt-web/data-service';
import { AdditionalAnalysisParams, BasePortfolio, PortfolioConstants, WweApiParamPositionImpl, WweBenchmarkPosition } from '@ddt-web/domain';
import { SecurityIdentifierUtil } from '@ddt-web/generic-utils';
import { AssetResolveResponse } from '@ddt-web/pas';
import { StoreService } from '@ddt-web/store-service';
import { PortfolioSecurityIdentifierMappingUtil, SecurityIdentifiersMapRequest, SecurityResolutionDetailsRequest } from '@ddt-web/wealth-web';
import { first } from 'rxjs/operators';

import { PasApiProcessingService } from '../pas-setup-data-workers/services/api-processing/pas-api-processing.service';
import { PasDataPortfolioAnalysisService } from '../pas-setup-data-workers/services/pas-data-portfolio-analysis.service';
import { InternalPortfolioResolverWorker } from '../workers/internal-portfolio-resolver-worker';
import { NileDataLoadingWorker } from '../workers/nile-data-loading-worker';


@Injectable({
	providedIn: 'root'
})
export class TransientPortfolioDataLoaderService {

	public configArray = [this.storeConfig.risk, this.storeConfig.performance, this.storeConfig.details];

	constructor(
		protected dataPortfolioAnalysisService: PasDataPortfolioAnalysisService,
		private dataLoadingWorker: NileDataLoadingWorker,
		private internalPortfolioResolverWorker: InternalPortfolioResolverWorker,
		@Inject(APP_STORE_CONFIG_TOKEN) public storeConfig: AppStoreConfig,
		private storeService: StoreService,
		private displayDataService: DisplayDataFactoryService,
		private pasApiProcessingService: PasApiProcessingService
	) { }

	public fetchExistingPortfolioAnalysis(portfolios: string[], additionalAnalysisParams: { currency: string }): void {
		this.internalPortfolioResolverWorker.handle({ portfolios, additionalAnalysisParams });
		this.dataLoadingWorker.handle({ portfolios, additionalAnalysisParams, forceAnalysis: true });
	}

	public handlePortfolioTransientAnalysis(portfolio: BasePortfolio, additionalAnalysisParams: AdditionalAnalysisParams, setDefaultIds?: boolean) {
		const portfolioId = portfolio.uniqueId;
		const children = portfolio.children;
		const positionMap: SecurityResolutionDetailsRequest = this.createPositionMap(children);
		// set loading state of the store
		this.storeService.fetch(this.storeConfig.positionDetails);

		this.pasApiProcessingService.getPositionDetailsResponse(
			[portfolioId], true, null, positionMap, PasFeature.TRANSIENT_ANALYSIS
		).pipe(first()).subscribe(res => {
			this.updatePositionsInStore(res, portfolio);
		});
		this.fetchTransientAnalysisData(portfolioId, children, additionalAnalysisParams, setDefaultIds);
	}

	private updatePositionsInStore(securityDetails: AssetResolveResponse, portfolio: BasePortfolio): void {
		const enrichedChildren = portfolio.children.map(child => {
			const childKey = PortfolioSecurityIdentifierMappingUtil.buildSecurityKey(child);
			return {
				...child,
				...securityDetails.assets.get(childKey)
			};
		});
		this.storeService.upsertOne(this.storeConfig.positionDetails, {
			id: portfolio.uniqueId,
			children: enrichedChildren
		});
	}

	public fetchTransientAnalysisData(id: string, positions: any[], additionalAnalysisParams: AdditionalAnalysisParams, setDefaultIds?: boolean): void {
		this.storeService.selectAll(this.storeConfig.associatedPortfolios).pipe(first()).subscribe(associatedPortfolios => {

			const wwePositions = this.createPositions(positions);
			const portfolioAnalysisParam = new PortfolioAnalysisParams(
				id ?? PortfolioConstants.TRANSIENT_PORTFOLIO_ID,
				null,
				wwePositions,
				null,
				additionalAnalysisParams.currency
			);
			this.pasApiProcessingService.getAnalysisParamsWithBenchmark(
				associatedPortfolios, null, [portfolioAnalysisParam], additionalAnalysisParams.currency, additionalAnalysisParams
			).pipe(first()).subscribe(analysisParams => {
				this.dataPortfolioAnalysisService.loadPortfolioAnalysisData(
					this.configArray,
					new AnalysisParams([portfolioAnalysisParam], analysisParams.benchmark)
				);
			});

			if (setDefaultIds) {
				this.displayDataService.setDefaultIds([id]);
			}
		});
	}

	public removeTransientPortfolioFromStores(): void {
		this.configArray.forEach(config => {
			this.storeService.removeMany(config, [PortfolioConstants.TRANSIENT_PORTFOLIO_ID]);
		});
	}

	private createPositionMap(positions: any[]): SecurityResolutionDetailsRequest {
		return {
			securities: positions.map(position => {
				return {
					id: PortfolioSecurityIdentifierMappingUtil.buildSecurityKey(position),
					identifiers: PortfolioSecurityIdentifierMappingUtil.buildIdentifiersMap(position)
				} as SecurityIdentifiersMapRequest;
			})
		};
	}

	private createPositions(positions: Array<HoldingTableDetails | WweBenchmarkPosition>): WweApiParamPositionImpl[] {
		return positions?.map(position => {
			return new WweApiParamPositionImpl(
				SecurityIdentifierUtil.buildSecurityAggregateKey(position),
				PortfolioSecurityIdentifierMappingUtil.buildIdentifiersMap(position),
				position['weight'],
				undefined
			);
		});
	}
}
